import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  NewspaperIcon, 
  CalendarIcon, 
  TrophyIcon, 
  PlusIcon,
  PhotoIcon,
  PlayIcon,
  UserGroupIcon,
  AcademicCapIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/apiService';
import TokenStatus from './components/TokenStatus';

// API configuration
const getApiUrl = (endpoint) => {
  const baseUrl = 'https://fciveria.ge';
  return `${baseUrl}${endpoint}`;
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    news: 0,
    matches: 0,
    standings: 0,
    players: 0,
    coaching_staff: 0,
    technical_staff: 0,
    financial_documents: 0,
    social_staff: 0
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        // Get token from the same source as apiService
        const storedUser = localStorage.getItem('fciveria_admin_user');
        const token = storedUser ? JSON.parse(storedUser).token : null;
        
        if (!token) {
          console.error('No auth token found');
          return;
        }
        
        const [news, matches, standings, photos, videos, players, coachingStaff, technicalStaff, financialDocs, socialStaff] = await Promise.all([
          apiService.getNews(),
          apiService.getMatches(),
          apiService.getStandings(),
          fetch(getApiUrl('/api/photos/admin'), {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(res => res.json()).catch(() => []),
          fetch(getApiUrl('/api/videos/admin'), {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(res => res.json()).catch(() => []),
          fetch(getApiUrl('/api/players/'), {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(res => res.json()).catch(() => []),
          fetch(getApiUrl('/api/coaching-staff/'), {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(res => res.json()).catch(() => []),
          fetch(getApiUrl('/api/technical-staff/'), {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(res => res.json()).catch(() => []),
          fetch(getApiUrl('/api/financial-documents/all'), {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(res => res.json()).catch(() => []),
          fetch(getApiUrl('/api/social-staff/admin'), {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(res => res.json()).catch(() => [])
        ]);
        
        setStats({
          news: news.length,
          matches: matches.length,
          standings: standings.length,
          photos: photos.length || 0,
          videos: videos.length || 0,
          players: players.length || 0,
          coaching_staff: coachingStaff.length || 0,
          technical_staff: technicalStaff.length || 0,
          financial_documents: financialDocs.length || 0,
          social_staff: socialStaff.length || 0
        });
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    };
    
    loadStats();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/_panel-7c39f8/login');
  };

  const statCards = [
    {
      title: 'ამბები',
      count: stats.news,
      icon: NewspaperIcon,
      color: 'bg-blue-500',
      href: '/_panel-7c39f8/news'
    },
    {
      title: 'მატჩები',
      count: stats.matches,
      icon: CalendarIcon,
      color: 'bg-green-500',
      href: '/_panel-7c39f8/matches'
    },
    {
      title: 'სტენდინგები',
      count: stats.standings,
      icon: TrophyIcon,
      color: 'bg-yellow-500',
      href: '/_panel-7c39f8/standings'
    },
    {
      title: 'ფოტოები',
      count: stats.photos || 0,
      icon: PhotoIcon,
      color: 'bg-purple-500',
      href: '/_panel-7c39f8/photos'
    },
    {
      title: 'ვიდეოები',
      count: stats.videos || 0,
      icon: PlayIcon,
      color: 'bg-red-500',
      href: '/_panel-7c39f8/videos'
    },
    {
      title: 'ფეხბურთელები',
      count: stats.players || 0,
      icon: UserGroupIcon,
      color: 'bg-indigo-500',
      href: '/_panel-7c39f8/players'
    },
    {
      title: 'სამწვრთნელო შტაბი',
      count: stats.coaching_staff || 0,
      icon: AcademicCapIcon,
      color: 'bg-orange-500',
      href: '/_panel-7c39f8/coaching-staff'
    },
    {
      title: 'ტექნიკური პერსონალი',
      count: stats.technical_staff || 0,
      icon: WrenchScrewdriverIcon,
      color: 'bg-teal-500',
      href: '/_panel-7c39f8/technical-staff'
    },
    {
      title: 'ფინანსური დოკუმენტები',
      count: stats.financial_documents || 0,
      icon: DocumentTextIcon,
      color: 'bg-gray-500',
      href: '/_panel-7c39f8/financial-documents'
    },
    {
      title: 'სოც მედია პერსონალი',
      count: stats.social_staff || 0,
      icon: UserIcon,
      color: 'bg-pink-500',
      href: '/_panel-7c39f8/social-staff'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <img 
                src="https://cup.gff.ge/sites/default/files/styles/logo_medium/public/2024-12/iveria.png?itok=FR6w_kKm" 
                alt="FC Iveria Logo" 
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="ml-3">
                <h1 className="text-2xl font-bold text-gray-900 font-georgian">
                  ადმინისტრაციული პანელი
                </h1>
                <p className="text-sm text-gray-600 font-georgian">
                  FC ივერია
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700 font-georgian">
                მოგესალმებით, {user?.name}
              </span>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium font-georgian"
              >
                გასვლა
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Token Status */}
        <TokenStatus />
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
          {statCards.map((stat) => (
            <div
              key={stat.title}
              className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(stat.href)}
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`${stat.color} p-3 rounded-md`}>
                      <stat.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate font-georgian">
                        {stat.title}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 font-georgian">
                        {stat.count}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 font-georgian mb-4">
              სწრაფი მოქმედებები
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <button
                onClick={() => navigate('/_panel-7c39f8/news/new')}
                className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-lg border border-gray-300 hover:border-gray-400"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-blue-50 text-blue-700 ring-4 ring-white">
                    <PlusIcon className="h-6 w-6" />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium font-georgian">
                    <span className="absolute inset-0" aria-hidden="true" />
                    ახალი ამბავი
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 font-georgian">
                    დაამატეთ ახალი ამბავი საიტზე
                  </p>
                </div>
              </button>

              <button
                onClick={() => navigate('/_panel-7c39f8/matches/new')}
                className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-lg border border-gray-300 hover:border-gray-400"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-green-50 text-green-700 ring-4 ring-white">
                    <PlusIcon className="h-6 w-6" />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium font-georgian">
                    <span className="absolute inset-0" aria-hidden="true" />
                    ახალი მატჩი
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 font-georgian">
                    დაამატეთ ახალი მატჩი
                  </p>
                </div>
              </button>

              <button
                onClick={() => navigate('/_panel-7c39f8/standings/new')}
                className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-lg border border-gray-300 hover:border-gray-400"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-yellow-50 text-yellow-700 ring-4 ring-white">
                    <PlusIcon className="h-6 w-6" />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium font-georgian">
                    <span className="absolute inset-0" aria-hidden="true" />
                    ახალი გუნდი
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 font-georgian">
                    დაამატეთ ახალი გუნდი სტენდინგებში
                  </p>
                </div>
              </button>

              <button
                onClick={() => navigate('/_panel-7c39f8/players/new')}
                className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-lg border border-gray-300 hover:border-gray-400"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-indigo-50 text-indigo-700 ring-4 ring-white">
                    <PlusIcon className="h-6 w-6" />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium font-georgian">
                    <span className="absolute inset-0" aria-hidden="true" />
                    ახალი ფეხბურთელი
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 font-georgian">
                    დაამატეთ ახალი ფეხბურთელი გუნდში
                  </p>
                </div>
              </button>

              <button
                onClick={() => navigate('/_panel-7c39f8/coaching-staff/new')}
                className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-lg border border-gray-300 hover:border-gray-400"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-orange-50 text-orange-700 ring-4 ring-white">
                    <PlusIcon className="h-6 w-6" />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium font-georgian">
                    <span className="absolute inset-0" aria-hidden="true" />
                    ახალი მწვრთნელი
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 font-georgian">
                    დაამატეთ ახალი მწვრთნელი შტაბში
                  </p>
                </div>
              </button>

              <button
                onClick={() => navigate('/_panel-7c39f8/technical-staff/new')}
                className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-lg border border-gray-300 hover:border-gray-400"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-teal-50 text-teal-700 ring-4 ring-white">
                    <PlusIcon className="h-6 w-6" />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium font-georgian">
                    <span className="absolute inset-0" aria-hidden="true" />
                    ახალი პერსონალი
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 font-georgian">
                    დაამატეთ ახალი ტექნიკური პერსონალი
                  </p>
                </div>
              </button>

              <button
                onClick={() => navigate('/_panel-7c39f8/financial-documents/new')}
                className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-lg border border-gray-300 hover:border-gray-400"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-gray-50 text-gray-700 ring-4 ring-white">
                    <PlusIcon className="h-6 w-6" />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium font-georgian">
                    <span className="absolute inset-0" aria-hidden="true" />
                    ახალი დოკუმენტი
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 font-georgian">
                    ატვირთეთ ახალი ფინანსური დოკუმენტი
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-8 bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 font-georgian mb-4">
              ბოლო აქტივობა
            </h3>
            <div className="flow-root">
              <ul className="-mb-8">
                <li>
                  <div className="relative pb-8">
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                          <NewspaperIcon className="h-5 w-5 text-white" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500 font-georgian">
                            <span className="font-medium text-gray-900">ახალი ამბავი</span> დაემატა
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500 font-georgian">
                          <time>2 საათის წინ</time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
                <li>
                  <div className="relative pb-8">
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                          <CalendarIcon className="h-5 w-5 text-white" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500 font-georgian">
                            <span className="font-medium text-gray-900">მატჩი</span> განახლდა
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500 font-georgian">
                          <time>4 საათის წინ</time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
